// src/screens/Admin/StudioAnalytics.js

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../api';
import backIcon from '../../assets/backspace-icon.png';

import './StudioAnalytics.css';

const ENTRY_LABELS = {
  edit_profile: 'Edit Profile',
  profile: 'Own profile picture',
  user_profile: 'Another user’s profile picture',

  post_fullscreen: 'Post fullscreen',
  home_awareness: 'Home drawer',
  unknown: 'Unknown / direct',
};

const EVENT_LABELS = {
  studio_opened: 'Studio visits',
  avatar_customization_started: 'Experimented visits',
  avatar_new_started: 'New avatar taps',
  avatar_edit_opened: 'Existing avatar edit opens',
  studio_entry_clicked: 'Studio entry clicks',
  avatar_post_composer_opened: 'Composer handoffs',
  avatar_created: 'New avatars saved',
  avatar_design_updated: 'Design updates saved',
  avatar_applied: 'Successful apply requests',

  avatar_post_published: 'Studio posts published',
  avatar_awareness_shown: 'Home drawer impressions',
  avatar_awareness_dismissed: 'Home drawer dismissals',
  avatar_awareness_started: 'Home drawer Start clicks',
};

const DAILY_COLUMNS = [
  ['studio_opened', 'Opened'],
  ['avatar_customization_started', 'Experimented'],
  ['avatar_created', 'Created'],
  ['avatar_applied', 'Applied'],
  ['avatar_post_published', 'Published'],
];

function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toLocaleString() : '0';
}

function rate(value) {
  return typeof value === 'number' && Number.isFinite(value)
    ? `${value.toFixed(1)}%`
    : '—';
}

function formatDate(value, includeTime = false) {
  if (!value) return 'Not available';

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Not available';

  return date.toLocaleString('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(includeTime
      ? { hour: '2-digit', minute: '2-digit' }
      : {}),
  });
}

function todayInJohannesburg() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(new Date())
      .map((part) => [part.type, part.value])
  );

  return `${parts.year}-${parts.month}-${parts.day}`;
}

function Metric({ label, value, note, isRate = false }) {
  return (
    <div className="sta-metric">
      <span className="sta-metric__label">{label}</span>
      <strong>
        {isRate ? rate(value) : number(value)}
      </strong>
      {note && <small>{note}</small>}
    </div>
  );
}

function TableWrap({ children }) {
  return (
    <div
      className="sta-table-wrap"
      tabIndex={0}
      role="region"
      aria-label="Scrollable analytics table"
    >
      {children}
    </div>
  );
}

export default function StudioAnalytics() {
  const navigate = useNavigate();

  const [mode, setMode] = useState('adoption');
  const [period, setPeriod] = useState('7');
  const [start, setStart] = useState(todayInJohannesburg);
  const [end, setEnd] = useState(todayInJohannesburg);
  const [customRange, setCustomRange] = useState(null);
  const [dateError, setDateError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      // Clear previous results so adoption/test data cannot be
      // displayed under a newly selected mode while loading.
      setData(null);
      setError('');

      if (period === 'custom' && !customRange) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const params = {
          mode,
          ...(period === 'custom' ? customRange : {}),
        };

        const response = await api.get(
          '/admin/studio-analytics/overview',
          {
            params,
            signal: controller.signal,
          }
        );

        if (cancelled) return;

        setData(response.data);
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;

        const status = err?.response?.status;

        setError(
          status === 401
            ? 'Please sign in again to view analytics.'
            : status === 403
              ? 'You do not have access to these metrics.'
              : err?.response?.data?.error ||
                'Could not load Avatar Studio analytics.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [mode, period, customRange, refreshKey]);

  function applyCustomDates(event) {
    event.preventDefault();
    setDateError('');

    if (!start || !end) {
      setDateError('Select both dates.');
      return;
    }

    if (end < start) {
      setDateError('End date must be on or after start date.');
      return;
    }

    if (start > todayInJohannesburg()) {
      setDateError('Start date cannot be in the future.');
      return;
    }

    const days =
      (Date.parse(`${end}T00:00:00Z`) -
        Date.parse(`${start}T00:00:00Z`)) /
        86400000 +
      1;

    if (!Number.isFinite(days) || days > 366) {
      setDateError('Select a maximum of 366 calendar days.');
      return;
    }

    setCustomRange({ start, end });
  }

  const selectedPeriod =
    period === 'custom'
      ? data?.customPeriod
      : period === '30'
        ? data?.last30Days
        : data?.last7Days;

  const events = selectedPeriod?.events || {};
  const outcomes = selectedPeriod?.visitorOutcomes || {};
  const snapshot = data?.snapshot || {};

  function eventMetric(eventType, field = 'total') {
    return events[eventType]?.[field] || 0;
  }

  const periodTitle =
    period === 'custom'
      ? 'Custom period'
      : `Last ${period} days`;

  return (
    <div className="sta-screen">
      <header className="sta-header">
        <button
          type="button"
          className="sta-back"
          aria-label="Back to admin dashboard"
          onClick={() => navigate('/admin')}
        >
          <img src={backIcon} alt="" />
        </button>

        <h1>Avatar Studio Analytics</h1>

        <button
          type="button"
          className="sta-refresh"
          disabled={loading}
          onClick={() => setRefreshKey((value) => value + 1)}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </header>

      <main className="sta-scroll">
        <section className="sta-controls" aria-label="Report filters">
          <label>
            Audience
            <select
              value={mode}
              onChange={(event) => setMode(event.target.value)}
            >
              <option value="adoption">
                Adoption · excludes test accounts
              </option>
              <option value="test">
                Test verification · excluded accounts only
              </option>
            </select>
          </label>

          <label>
            Period
            <select
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="custom">Custom dates</option>
            </select>
          </label>

          {period === 'custom' && (
            <form
              className="sta-date-form"
              onSubmit={applyCustomDates}
            >
              <label>
                Start date
                <input
                  type="date"
                  value={start}
                  max={todayInJohannesburg()}
                  onChange={(event) => setStart(event.target.value)}
                  required
                />
              </label>

              <label>
                End date
                <input
                  type="date"
                  value={end}
                  max={todayInJohannesburg()}
                  onChange={(event) => setEnd(event.target.value)}
                  required
                />
              </label>

              <button type="submit">Apply dates</button>

              {dateError && (
                <p className="sta-error" role="alert">
                  {dateError}
                </p>
              )}
            </form>
          )}
        </section>

        <p className={`sta-mode-note ${mode === 'test' ? 'is-test' : ''}`}>
          {mode === 'test'
            ? 'Test verification: only excluded accounts are counted.'
            :'Adoption: configured internal and test accounts are excluded.'}
        </p>

        {loading && (
          <p className="sta-state" role="status">
            Loading Studio analytics…
          </p>
        )}

        {error && (
          <div className="sta-state sta-error" role="alert">
            <p>{error}</p>
            <button
              type="button"
              onClick={() => setRefreshKey((value) => value + 1)}
            >
              Try again
            </button>
          </div>
        )}

        {!loading &&
          !error &&
          period === 'custom' &&
          !customRange && (
            <p className="sta-state">
              Select your dates and tap Apply dates.
            </p>
          )}

        {!loading && !error && data && selectedPeriod && (
          <>
            <div className="sta-report-meta">
              <h2>{periodTitle}</h2>
              <p>
                {formatDate(selectedPeriod.since, true)}
                {' → '}
                {formatDate(selectedPeriod.until, true)}
              </p>
              <p>
                Reporting timezone: {data.timezone}.
                Updated {formatDate(data.generatedAt, true)}.
              </p>
              <p>
                First recorded event:{' '}
                {formatDate(data.firstRecordedEventAt, true)}.
                Tracking coverage may differ by event.
              </p>
            </div>


            <section className="sta-section">
              <h2>Home avatar introduction</h2>

              <div className="sta-grid">
                <Metric
                  label="Drawer impressions"
                  value={eventMetric('avatar_awareness_shown')}
                />
                <Metric
                  label="Unique users shown"
                  value={eventMetric(
                    'avatar_awareness_shown',
                    'uniqueUsers'
                  )}
                />
                <Metric
                  label="Start clicks"
                  value={eventMetric('avatar_awareness_started')}
                />
                <Metric
                  label="Unique Start clickers"
                  value={eventMetric(
                    'avatar_awareness_started',
                    'uniqueUsers'
                  )}
                />
                <Metric
                  label="Dismissals"
                  value={eventMetric('avatar_awareness_dismissed')}
                />
                <Metric
                  label="Unique dismissers"
                  value={eventMetric(
                    'avatar_awareness_dismissed',
                    'uniqueUsers'
                  )}
                />
                <Metric
                  label="Studio opens from Home drawer"
                  value={
                    selectedPeriod.entryPoints
                      ?.home_awareness?.opens
                  }
                />
                <Metric
                  label="Unique Studio visitors from drawer"
                  value={
                    selectedPeriod.entryPoints
                      ?.home_awareness?.uniqueVisitors
                  }
                />
              </div>

              <p className="sta-note">
                Impressions count recorded drawer appearances.
                Start clicks and Studio opens are recorded separately.
                These period totals are not a matched conversion funnel.
              </p>
            </section>

            <section className="sta-section">
              <h2>Discovery and experimentation</h2>


              <div className="sta-grid">
                <Metric
                  label="Unique Studio visitors"
                  value={eventMetric('studio_opened', 'uniqueUsers')}
                />
                <Metric
                  label="Studio visits"
                  value={eventMetric('studio_opened')}
                />
                <Metric
                  label="Unique experimenters"
                  value={eventMetric(
                    'avatar_customization_started',
                    'uniqueUsers'
                  )}
                />
                <Metric
                  label="Experimented visits"
                  value={eventMetric('avatar_customization_started')}
                  note="First actual option change per visit"
                />
                <Metric
                  label="Repeat visitors"
                  value={selectedPeriod.repeatVisitors}
                  note="Opened on at least two different days in this period"
                />
              </div>
            </section>

            <section className="sta-section">
              <h2>Creation and profile use</h2>
              <div className="sta-grid">
                <Metric
                  label="New avatars saved"
                  value={eventMetric('avatar_created')}
                />
                <Metric
                  label="Unique creators"
                  value={eventMetric('avatar_created', 'uniqueUsers')}
                />
                <Metric
                  label="Design updates saved"
                  value={eventMetric('avatar_design_updated')}
                  note="Renames are excluded"
                />
                <Metric
                  label="Successful apply requests"
                  value={eventMetric('avatar_applied')}
                  note="Reapplying can count again"
                />
                <Metric
                  label="Unique appliers"
                  value={eventMetric('avatar_applied', 'uniqueUsers')}
                />
              </div>
            </section>

            <section className="sta-section">
              <h2>Sharing</h2>
              <div className="sta-grid">
                <Metric
                  label="Composer handoffs"
                  value={eventMetric('avatar_post_composer_opened')}
                  note="Does not mean a post was published"
                />
                <Metric
                  label="Studio posts published"
                  value={eventMetric('avatar_post_published')}
                />
                <Metric
                  label="Studio images published"
                  value={eventMetric(
                    'avatar_post_published',
                    'studioImages'
                  )}
                />
                <Metric
                  label="Unique publishers"
                  value={eventMetric(
                    'avatar_post_published',
                    'uniqueUsers'
                  )}
                />
              </div>
            </section>

            <section className="sta-section">
              <h2>Outcomes among tracked visitors</h2>
              <p className="sta-note">
                Of {number(outcomes.visitors)} users who opened Studio
                in this period, these users acted at or after their
                first open. These are separate outcomes, not sequential
                funnel steps or proof that a CTA caused the action.
              </p>

              <div className="sta-grid">
                <Metric
                  label="Experimented"
                  value={outcomes.experimented}
                />
                <Metric
                  label="Experiment rate"
                  value={outcomes.experimentRate}
                  isRate
                />
                <Metric label="Created" value={outcomes.created} />
                <Metric
                  label="Create rate"
                  value={outcomes.createRate}
                  isRate
                />
                <Metric label="Applied" value={outcomes.applied} />
                <Metric
                  label="Apply rate"
                  value={outcomes.applyRate}
                  isRate
                />
                <Metric label="Published" value={outcomes.published} />
                <Metric
                  label="Publish rate"
                  value={outcomes.publishRate}
                  isRate
                />
              </div>
            </section>

            <section className="sta-section">
              <h2>Where users entered Studio</h2>
              <p className="sta-note">
                Clicks and opens are recorded separately. One user may
                use several entry points, so row totals are not an
                overall unique-user count.
              </p>

              <TableWrap>
                <table className="sta-table">
                  <caption>Studio entry points</caption>
                  <thead>
                    <tr>
                      <th scope="col">Entry point</th>
                      <th scope="col">Clicks</th>
                      <th scope="col">Unique clickers</th>
                      <th scope="col">Opens</th>
                      <th scope="col">Unique visitors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(ENTRY_LABELS).map(([key, label]) => {
                      const row = selectedPeriod.entryPoints?.[key];

                      return (
                        <tr key={key}>
                          <th scope="row">{label}</th>
                          <td>{number(row?.clicks)}</td>
                          <td>{number(row?.uniqueClickers)}</td>
                          <td>{number(row?.opens)}</td>
                          <td>{number(row?.uniqueVisitors)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableWrap>
            </section>

            <section className="sta-section">
              <h2>Daily activity</h2>
              <p className="sta-note">
                Each figure counts unique users that day. Daily totals
                cannot be added to obtain unique users for the period.

                The first and last day may be partial.
                Any activity also includes Home drawer impressions
                and interactions; it does not necessarily mean
                the user opened Studio.
              </p>

              <TableWrap>
                <table className="sta-table">
                  <caption>Daily unique users by activity</caption>
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Any activity</th>
                      {DAILY_COLUMNS.map(([key, label]) => (
                        <th scope="col" key={key}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedPeriod.daily || []).map((row) => (
                      <tr key={row.date}>
                        <th scope="row">{row.date}</th>
                        <td>{number(row.activeUsers)}</td>
                        {DAILY_COLUMNS.map(([key]) => (
                          <td key={key}>
                            {number(row.events?.[key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            </section>

            <section className="sta-section">
              <h2>Current collection and profile snapshot</h2>
              <p className="sta-note">
                Across all dates for the selected audience. These
                figures are not restricted to {periodTitle.toLowerCase()}.
                Deleted creations are absent.
              </p>

              <div className="sta-grid">
                <Metric
                  label="Saved creations"
                  value={snapshot.savedCreations}
                />
                <Metric
                  label="Users with saved creations"
                  value={snapshot.creatorsWithSavedCreations}
                />
                <Metric
                  label="Users with equipped association"
                  value={snapshot.usersWithEquippedAssociation}
                  note="Pictures retained after clearing the association are not included"
                />
              </div>
            </section>

            <details className="sta-section">
              <summary>All recorded events · verification</summary>
              <TableWrap>
                <table className="sta-table">
                  <caption>Recorded event totals</caption>
                  <thead>
                    <tr>
                      <th scope="col">Event</th>
                      <th scope="col">Total</th>
                      <th scope="col">Unique users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(EVENT_LABELS).map(([key, label]) => (
                      <tr key={key}>
                        <th scope="row">{label}</th>
                        <td>{number(events[key]?.total)}</td>
                        <td>{number(events[key]?.uniqueUsers)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            </details>

            <details className="sta-section">
              <summary>Definitions and excluded accounts</summary>

              <p className="sta-note">
                Recorded events remain after later deletion of a
                creation or post. Missing historical events cannot be
                reconstructed from these totals. A dash means no
                denominator is available for a rate.
              </p>

              <dl className="sta-definitions">
                {Object.entries(data.definitions || {}).map(
                  ([key, description]) => (
                    <div key={key}>
                      <dt>{key}</dt>
                      <dd>{description}</dd>
                    </div>
                  )
                )}
              </dl>

              <p>
                Excluded accounts:{' '}
                {(data.excludedUsernames || []).join(', ')}.
              </p>
              <p>
                Matching current accounts:{' '}
                {number(data.resolvedExcludedAccounts)}.
              </p>
            </details>
          </>
        )}
      </main>
    </div>
  );
}
